import {useEffect} from "react";
import {useDispatch} from "react-redux";
import {css, cx} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {type IModalOverlayVariant, Modal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {hitUserSegmentBudget, hitUserSegmentLocationEntry, hitUserSegmentRooms} from "@pg-mono/google-tag-manager";
import {PREFERENCES_SURVEY_MODAL_TEST_IDS} from "@pg-mono/test-ids/src/rp/common/test_id";
import {Currency, PreferencesSurveySource} from "@pg-rp/common-types";

import {IApplicationData} from "../../../application/types/IApplicationData";
import {useGetClientInfoQuery} from "../../../client/api/get_client_info";
import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {useCreateForYouClientMutation, useUpdateForYouClientMutation} from "../../../for_you/api/for_you_client";
import {addWarningNotification} from "../../../notifications/utils/add_warning_notification";
import {useLazyGetRegionDetailListQuery} from "../../../region/api/get_region_detail_list";
import {useAppSelector} from "../../../store/hooks";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useGetHomeMatchConfigurationQuery} from "../../../user/api/patch_home_match_configuration";
import {UserPreferencesFormProvider} from "../../../user/components/preferences/UserPreferencesFormProvider";
import {PreferencesFormWithPriceHeader} from "../../../user/components/preferences_form/PreferencesFormWithPriceHeader";
import {PreferencesForm} from "../../../user/components/PreferencesForm";
import {PREFERENCES_FORM_MAX_DEFAULT} from "../../../user/constants/preferences/application_preferences_form";
import {useSurveyDefaultValues} from "../../../user/hooks/use_survey_default_values";
import {hitGtmSurveyBasicSubmit} from "../../../user/tracking/preferences/preferences_hit_survey_basic_submit";
import {hitGtmSurveyClick} from "../../../user/tracking/preferences/preferences_hit_survey_click";
import {hitGtmSurveyClose} from "../../../user/tracking/preferences/preferences_hit_survey_close";
import {hitGtmSurveyView} from "../../../user/tracking/preferences/preferences_hit_survey_view";
import {IPreferencesDefaultValues} from "../../../user/types/IPreferencesDefaultValues";
import {buildHomeMatchBasicClientPayload, getHomeMatchBasicContactData} from "../../../user/utils/preferences/home_match_basic_preferences";
import {savePreferences} from "../../state/registration_flow_slice";
import {AuthModalVariant} from "../auth_modal/AuthModalHeaderVariants";

type RegistrationPreferencesModalLayoutVariant = "standard" | "homeMatchBasicSurvey" | "homeMatchBasicEdit";

interface IProps {
    isOpen: boolean;
    isPropertyApplication: boolean;
    offerId?: number;
    propertyId?: number | null;
    application?: IApplicationData | null;
    onSuccess?: (values: IPreferencesDefaultValues, isExplicitSubmit: boolean) => void;
    onCancel?: () => void;
    surveySource?: PreferencesSurveySource;
    title?: string;
    description?: string;
    submitButtonText?: string;
    closeButtonText?: string;
    showPriceHeader?: boolean;
    showCreditPlan?: boolean;
    layoutVariant?: RegistrationPreferencesModalLayoutVariant;
    overrideDefaultValues?: IPreferencesDefaultValues;
    gtmEventCategory?: string;
    gtmCustomLabel?: string;
    overlayVariant?: IModalOverlayVariant;
    zIndex?: number;
}

export const RegistrationPreferencesModal = (props: IProps) => {
    const {
        isOpen,
        isPropertyApplication,
        offerId,
        propertyId,
        application,
        onSuccess,
        onCancel,
        surveySource = PreferencesSurveySource.PREFERENCES,
        title = "Potwierdź swoje preferencje",
        description = "Potwierdź swój budżet, lokalizację i liczbę pokoi, aby otrzymać lepiej spersonalizowane oferty.",
        submitButtonText,
        closeButtonText,
        showPriceHeader = true,
        showCreditPlan = true,
        layoutVariant = "standard",
        overrideDefaultValues,
        gtmEventCategory,
        gtmCustomLabel,
        overlayVariant,
        zIndex
    } = props;
    const isHomeMatchBasicSurveyLayout = layoutVariant === "homeMatchBasicSurvey";
    const isHomeMatchBasicEditLayout = layoutVariant === "homeMatchBasicEdit";
    const dispatch = useDispatch();
    const [postClient, {isLoading: isPostLoading}] = useCreateForYouClientMutation();
    const [updateClient, {isLoading: isUpdateLoading}] = useUpdateForYouClientMutation();

    const {data: userProfile} = useGetUserProfileQuery();
    const {data: homeMatchConfig} = useGetHomeMatchConfigurationQuery();

    const {data: clientInfo} = useGetClientInfoQuery();
    const [fetchRegionDetails] = useLazyGetRegionDetailListQuery();

    const flowState = useAppSelector((state) => state.registrationFlow);

    const maxBudget = PREFERENCES_FORM_MAX_DEFAULT;
    const currency = Currency.PLN;

    const {defaultValues, displayedPrice, isLoading} = useSurveyDefaultValues({maxBudget, propertyId, offerId});

    const getEventCategory = (): string => {
        if (flowState.userSource === UserSource.FAVOURITE_PROPERTY || flowState.userSource === UserSource.FAVOURITE_OFFER) {
            return "favourite";
        }

        if (flowState.socialMediaType === "google") {
            return "google";
        }
        if (flowState.socialMediaType === "facebook") {
            return "facebook";
        }

        return "email";
    };

    const eventCategory = getEventCategory();
    const resolvedEventCategory = gtmEventCategory || flowState.gtmSourceSection || eventCategory;

    useEffect(() => {
        if (isOpen) {
            hitGtmSurveyView(resolvedEventCategory, gtmCustomLabel);
        }
    }, [gtmCustomLabel, isOpen, resolvedEventCategory]);

    const onSubmit = async (values: IPreferencesDefaultValues, isExplicitSubmit = false) => {
        const contactData = getHomeMatchBasicContactData(userProfile, application);
        const priceUpperForSegment = values.budget != null && values.budget < maxBudget ? values.budget : null;

        const regionOptions = (values.regions?.length ? values.regions : values.regionSelector) ?? [];
        const regionIds = regionOptions.map((r) => r.value).filter((id): id is number => typeof id === "number");

        hitUserSegmentRooms(values.rooms ?? {});
        hitUserSegmentBudget({priceUpper: priceUpperForSegment});

        if (regionIds.length > 0) {
            const regionsDetail = await fetchRegionDetails({regionIds}).unwrap();
            hitUserSegmentLocationEntry(regionsDetail);
        }

        if (!contactData) {
            dispatch(addWarningNotification("Nie można pobrać danych profilu. Spróbuj ponownie później."));
            return;
        }

        if (isExplicitSubmit) {
            hitGtmSurveyBasicSubmit(values, resolvedEventCategory, gtmCustomLabel);
        }

        if (!isExplicitSubmit) {
            hitGtmSurveyClose(resolvedEventCategory, gtmCustomLabel);
        }

        const regions = values.regionSelector.map((region) => region.value);

        const lower = typeof values.rooms.lower === "number" ? values.rooms.lower : 1;
        const upper = typeof values.rooms.upper === "number" ? values.rooms.upper : 5;
        const rooms = {lower, upper};

        const postData = buildHomeMatchBasicClientPayload({
            values,
            contactData,
            maxBudget,
            surveySource,
            isExplicitSubmit
        });

        try {
            if (!!clientInfo) {
                await updateClient(postData).unwrap();
            } else {
                await postClient(postData).unwrap();
            }

            dispatch(
                savePreferences({
                    regions: regions.length ? regions : [],
                    price: !values.budget || values.budget >= maxBudget ? undefined : values.budget,
                    rooms: rooms
                })
            );

            onSuccess?.(values, isExplicitSubmit);
        } catch (err) {
            notifyBugsnag(err, "RegistrationPreferencesModal save failed");

            dispatch(addWarningNotification("Coś poszło nie tak. Spróbuj ponownie później."));
        }
    };

    const handleFieldInteraction = (section: string, value: string) => {
        hitGtmSurveyClick(resolvedEventCategory, value, section, gtmCustomLabel);
    };
    const isAfterLeadWithHomeMatchOn = Boolean(homeMatchConfig?.is_active && (flowState.useLeadAfterRegistration || flowState.unlockChoiceLeadSent));
    const shouldShowPriceHeader =
        showPriceHeader && !isAfterLeadWithHomeMatchOn && (isHomeMatchBasicSurveyLayout || flowState.titleVariant !== AuthModalVariant.CONSTRUCTION_PHOTOS);

    return (
        <Modal
            isOpen={isOpen}
            className={cx(modalRootStyle, isHomeMatchBasicEditLayout && homeMatchBasicEditModalRootStyle)}
            onModalClose={isHomeMatchBasicEditLayout && onCancel ? () => onCancel() : undefined}
            overlayVariant={overlayVariant}
            {...(zIndex === undefined ? {} : {zIndex})}
        >
            <div data-testid={PREFERENCES_SURVEY_MODAL_TEST_IDS.WRAPPER}>
                <Modal.Layout.EmptyResponsive
                    closeButtonVariant={isHomeMatchBasicEditLayout ? "rounded" : "none"}
                    className={cx(modalStyle, isHomeMatchBasicEditLayout && homeMatchBasicEditModalStyle)}
                >
                    <div className={cx(contentStyle, isHomeMatchBasicEditLayout && homeMatchBasicEditContentStyle)}>
                        {/* We need to wait until `defaultValues` is built, so that the form gets the correct values */}
                        {(isLoading && !overrideDefaultValues) || isPostLoading || isUpdateLoading ? (
                            <div className={loaderWrapperStyle}>
                                <Loader size="lg" />
                            </div>
                        ) : (
                            <UserPreferencesFormProvider defaultValues={overrideDefaultValues ?? defaultValues}>
                                {() => {
                                    return (
                                        <PreferencesForm
                                            onSubmit={(values, isExplicitSubmit) => onSubmit(values, isExplicitSubmit)}
                                            header={
                                                shouldShowPriceHeader ? (
                                                    <div data-testid={PREFERENCES_SURVEY_MODAL_TEST_IDS.HEADER_BOX}>
                                                        <PreferencesFormWithPriceHeader
                                                            isPropertyApplication={isPropertyApplication}
                                                            offerId={offerId}
                                                            price={displayedPrice}
                                                        />
                                                    </div>
                                                ) : null
                                            }
                                            topFormContent={
                                                <div className={headerStyle}>
                                                    <div className={headerTextStyle}>
                                                        <Text variant={isHomeMatchBasicEditLayout ? "headline_3" : "headline_6"} as="p">
                                                            {title}
                                                        </Text>
                                                        {!isHomeMatchBasicEditLayout && description ? <Text variant="info_txt_1">{description}</Text> : null}
                                                    </div>
                                                </div>
                                            }
                                            currency={currency}
                                            maxBudget={maxBudget}
                                            formWrapperClassName={cx(
                                                formWrapperClassName,
                                                isHomeMatchBasicEditLayout && homeMatchBasicEditFormWrapperClassName
                                            )}
                                            onFieldInteraction={handleFieldInteraction}
                                            showCreditPlan={showCreditPlan}
                                            actionsStickyToBottomOnMobile={isHomeMatchBasicEditLayout}
                                            locationFieldTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.LOCATION_FIELD}
                                            budgetInputTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.BUDGET_INPUT}
                                            creditPlanSwitchTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.CREDIT_PLAN_SWITCH}
                                            savePreferencesButtonTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.SAVE_PREFERENCES_BUTTON}
                                            closeButtonTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.CLOSE_BUTTON}
                                            submitButtonText={submitButtonText}
                                            closeButtonText={closeButtonText}
                                            onCancel={onCancel}
                                        />
                                    );
                                }}
                            </UserPreferencesFormProvider>
                        )}
                    </div>
                </Modal.Layout.EmptyResponsive>
            </div>
        </Modal>
    );
};

const modalRootStyle = css({
    sm: {
        borderRadius: 2
    }
});

const homeMatchBasicEditModalRootStyle = css({
    sm: {
        width: "544px"
    }
});

const modalStyle = css({
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    p: 0,

    sm: {
        width: "544px"
    }
});

const homeMatchBasicEditModalStyle = css({
    sm: {
        width: "544px"
    }
});

const contentStyle = css({
    width: "100%",
    p: 0,
    backgroundColor: "var(--colors-white)",
    display: "flex",
    flexDirection: "column",
    gap: 4,

    sm: {
        width: "544px",
        p: 3
    }
});

const homeMatchBasicEditContentStyle = css({
    sm: {
        width: "544px",
        p: 3
    }
});

const loaderWrapperStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    minHeight: "100vh",

    sm: {
        minHeight: "300px"
    }
});

const formWrapperClassName = css({
    p: 2,

    md: {
        p: 0
    }
});

const homeMatchBasicEditFormWrapperClassName = css({
    p: 3,

    sm: {
        p: 0
    }
});

const headerStyle = css({
    display: "flex",
    gap: 2,
    alignItems: "flex-start"
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});
import {useEffect} from "react";
import {useDispatch} from "react-redux";
import {css, cx} from "@panda/css";

import {Loader} from "@pg-design/loader-module";
import {type IModalOverlayVariant, Modal} from "@pg-design/modal-module";
import {Text} from "@pg-design/text-module";
import {hitUserSegmentBudget, hitUserSegmentLocationEntry, hitUserSegmentRooms} from "@pg-mono/google-tag-manager";
import {PREFERENCES_SURVEY_MODAL_TEST_IDS} from "@pg-mono/test-ids/src/rp/common/test_id";
import {Currency, PreferencesSurveySource} from "@pg-rp/common-types";

import {IApplicationData} from "../../../application/types/IApplicationData";
import {useGetClientInfoQuery} from "../../../client/api/get_client_info";
import {notifyBugsnag} from "../../../errors/bugsnag/notify_bugsnag";
import {useCreateForYouClientMutation, useUpdateForYouClientMutation} from "../../../for_you/api/for_you_client";
import {addWarningNotification} from "../../../notifications/utils/add_warning_notification";
import {useLazyGetRegionDetailListQuery} from "../../../region/api/get_region_detail_list";
import {useAppSelector} from "../../../store/hooks";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {useGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useGetHomeMatchConfigurationQuery} from "../../../user/api/patch_home_match_configuration";
import {UserPreferencesFormProvider} from "../../../user/components/preferences/UserPreferencesFormProvider";
import {PreferencesFormWithPriceHeader} from "../../../user/components/preferences_form/PreferencesFormWithPriceHeader";
import {PreferencesForm} from "../../../user/components/PreferencesForm";
import {PREFERENCES_FORM_MAX_DEFAULT} from "../../../user/constants/preferences/application_preferences_form";
import {useSurveyDefaultValues} from "../../../user/hooks/use_survey_default_values";
import {hitGtmSurveyBasicSubmit} from "../../../user/tracking/preferences/preferences_hit_survey_basic_submit";
import {hitGtmSurveyClick} from "../../../user/tracking/preferences/preferences_hit_survey_click";
import {hitGtmSurveyClose} from "../../../user/tracking/preferences/preferences_hit_survey_close";
import {hitGtmSurveyView} from "../../../user/tracking/preferences/preferences_hit_survey_view";
import {IPreferencesDefaultValues} from "../../../user/types/IPreferencesDefaultValues";
import {buildHomeMatchBasicClientPayload, getHomeMatchBasicContactData} from "../../../user/utils/preferences/home_match_basic_preferences";
import {savePreferences} from "../../state/registration_flow_slice";
import {AuthModalVariant} from "../auth_modal/AuthModalHeaderVariants";

type RegistrationPreferencesModalLayoutVariant = "standard" | "homeMatchBasicSurvey" | "homeMatchBasicEdit";

interface IProps {
    isOpen: boolean;
    isPropertyApplication: boolean;
    offerId?: number;
    propertyId?: number | null;
    application?: IApplicationData | null;
    onSuccess?: (values: IPreferencesDefaultValues, isExplicitSubmit: boolean) => void;
    onCancel?: () => void;
    surveySource?: PreferencesSurveySource;
    title?: string;
    description?: string;
    submitButtonText?: string;
    closeButtonText?: string;
    showPriceHeader?: boolean;
    showCreditPlan?: boolean;
    layoutVariant?: RegistrationPreferencesModalLayoutVariant;
    overrideDefaultValues?: IPreferencesDefaultValues;
    gtmEventCategory?: string;
    gtmCustomLabel?: string;
    overlayVariant?: IModalOverlayVariant;
    zIndex?: number;
}

export const RegistrationPreferencesModal = (props: IProps) => {
    const {
        isOpen,
        isPropertyApplication,
        offerId,
        propertyId,
        application,
        onSuccess,
        onCancel,
        surveySource = PreferencesSurveySource.PREFERENCES,
        title = "Potwierdź swoje preferencje",
        description = "Potwierdź swój budżet, lokalizację i liczbę pokoi, aby otrzymać lepiej spersonalizowane oferty.",
        submitButtonText,
        closeButtonText,
        showPriceHeader = true,
        showCreditPlan = true,
        layoutVariant = "standard",
        overrideDefaultValues,
        gtmEventCategory,
        gtmCustomLabel,
        overlayVariant,
        zIndex
    } = props;
    const isHomeMatchBasicSurveyLayout = layoutVariant === "homeMatchBasicSurvey";
    const isHomeMatchBasicEditLayout = layoutVariant === "homeMatchBasicEdit";
    const dispatch = useDispatch();
    const [postClient, {isLoading: isPostLoading}] = useCreateForYouClientMutation();
    const [updateClient, {isLoading: isUpdateLoading}] = useUpdateForYouClientMutation();

    const {data: userProfile} = useGetUserProfileQuery();
    const {data: homeMatchConfig} = useGetHomeMatchConfigurationQuery();

    const {data: clientInfo} = useGetClientInfoQuery();
    const [fetchRegionDetails] = useLazyGetRegionDetailListQuery();

    const flowState = useAppSelector((state) => state.registrationFlow);

    const maxBudget = PREFERENCES_FORM_MAX_DEFAULT;
    const currency = Currency.PLN;

    const {defaultValues, displayedPrice, isLoading} = useSurveyDefaultValues({maxBudget, propertyId, offerId});

    const getEventCategory = (): string => {
        if (flowState.userSource === UserSource.FAVOURITE_PROPERTY || flowState.userSource === UserSource.FAVOURITE_OFFER) {
            return "favourite";
        }

        if (flowState.socialMediaType === "google") {
            return "google";
        }
        if (flowState.socialMediaType === "facebook") {
            return "facebook";
        }

        return "email";
    };

    const eventCategory = getEventCategory();
    const resolvedEventCategory = gtmEventCategory || flowState.gtmSourceSection || eventCategory;

    useEffect(() => {
        if (isOpen) {
            hitGtmSurveyView(resolvedEventCategory, gtmCustomLabel);
        }
    }, [gtmCustomLabel, isOpen, resolvedEventCategory]);

    const onSubmit = async (values: IPreferencesDefaultValues, isExplicitSubmit = false) => {
        const contactData = getHomeMatchBasicContactData(userProfile, application);
        const priceUpperForSegment = values.budget != null && values.budget < maxBudget ? values.budget : null;

        const regionOptions = (values.regions?.length ? values.regions : values.regionSelector) ?? [];
        const regionIds = regionOptions.map((r) => r.value).filter((id): id is number => typeof id === "number");

        hitUserSegmentRooms(values.rooms ?? {});
        hitUserSegmentBudget({priceUpper: priceUpperForSegment});

        if (regionIds.length > 0) {
            const regionsDetail = await fetchRegionDetails({regionIds}).unwrap();
            hitUserSegmentLocationEntry(regionsDetail);
        }

        if (!contactData) {
            dispatch(addWarningNotification("Nie można pobrać danych profilu. Spróbuj ponownie później."));
            return;
        }

        if (isExplicitSubmit) {
            hitGtmSurveyBasicSubmit(values, resolvedEventCategory, gtmCustomLabel);
        }

        if (!isExplicitSubmit) {
            hitGtmSurveyClose(resolvedEventCategory, gtmCustomLabel);
        }

        const regions = values.regionSelector.map((region) => region.value);

        const lower = typeof values.rooms.lower === "number" ? values.rooms.lower : 1;
        const upper = typeof values.rooms.upper === "number" ? values.rooms.upper : 5;
        const rooms = {lower, upper};

        const postData = buildHomeMatchBasicClientPayload({
            values,
            contactData,
            maxBudget,
            surveySource,
            isExplicitSubmit
        });

        try {
            if (!!clientInfo) {
                await updateClient(postData).unwrap();
            } else {
                await postClient(postData).unwrap();
            }

            dispatch(
                savePreferences({
                    regions: regions.length ? regions : [],
                    price: !values.budget || values.budget >= maxBudget ? undefined : values.budget,
                    rooms: rooms
                })
            );

            onSuccess?.(values, isExplicitSubmit);
        } catch (err) {
            notifyBugsnag(err, "RegistrationPreferencesModal save failed");

            dispatch(addWarningNotification("Coś poszło nie tak. Spróbuj ponownie później."));
        }
    };

    const handleFieldInteraction = (section: string, value: string) => {
        hitGtmSurveyClick(resolvedEventCategory, value, section, gtmCustomLabel);
    };
    const isAfterLeadWithHomeMatchOn = Boolean(homeMatchConfig?.is_active && (flowState.useLeadAfterRegistration || flowState.unlockChoiceLeadSent));
    const shouldShowPriceHeader =
        showPriceHeader && !isAfterLeadWithHomeMatchOn && (isHomeMatchBasicSurveyLayout || flowState.titleVariant !== AuthModalVariant.CONSTRUCTION_PHOTOS);

    return (
        <Modal
            isOpen={isOpen}
            className={cx(modalRootStyle, isHomeMatchBasicEditLayout && homeMatchBasicEditModalRootStyle)}
            onModalClose={isHomeMatchBasicEditLayout && onCancel ? () => onCancel() : undefined}
            overlayVariant={overlayVariant}
            {...(zIndex === undefined ? {} : {zIndex})}
        >
            <div data-testid={PREFERENCES_SURVEY_MODAL_TEST_IDS.WRAPPER}>
                <Modal.Layout.EmptyResponsive
                    closeButtonVariant={isHomeMatchBasicEditLayout ? "rounded" : "none"}
                    className={cx(modalStyle, isHomeMatchBasicEditLayout && homeMatchBasicEditModalStyle)}
                >
                    <div className={cx(contentStyle, isHomeMatchBasicEditLayout && homeMatchBasicEditContentStyle)}>
                        {/* We need to wait until `defaultValues` is built, so that the form gets the correct values */}
                        {(isLoading && !overrideDefaultValues) || isPostLoading || isUpdateLoading ? (
                            <div className={loaderWrapperStyle}>
                                <Loader size="lg" />
                            </div>
                        ) : (
                            <UserPreferencesFormProvider defaultValues={overrideDefaultValues ?? defaultValues}>
                                {() => {
                                    return (
                                        <PreferencesForm
                                            onSubmit={(values, isExplicitSubmit) => onSubmit(values, isExplicitSubmit)}
                                            header={
                                                shouldShowPriceHeader ? (
                                                    <div data-testid={PREFERENCES_SURVEY_MODAL_TEST_IDS.HEADER_BOX}>
                                                        <PreferencesFormWithPriceHeader
                                                            isPropertyApplication={isPropertyApplication}
                                                            offerId={offerId}
                                                            price={displayedPrice}
                                                        />
                                                    </div>
                                                ) : null
                                            }
                                            topFormContent={
                                                <div className={headerStyle}>
                                                    <div className={headerTextStyle}>
                                                        <Text variant={isHomeMatchBasicEditLayout ? "headline_3" : "headline_6"} as="p">
                                                            {title}
                                                        </Text>
                                                        {!isHomeMatchBasicEditLayout && description ? <Text variant="info_txt_1">{description}</Text> : null}
                                                    </div>
                                                </div>
                                            }
                                            currency={currency}
                                            maxBudget={maxBudget}
                                            formWrapperClassName={cx(
                                                formWrapperClassName,
                                                isHomeMatchBasicEditLayout && homeMatchBasicEditFormWrapperClassName
                                            )}
                                            onFieldInteraction={handleFieldInteraction}
                                            showCreditPlan={showCreditPlan}
                                            actionsStickyToBottomOnMobile={isHomeMatchBasicEditLayout}
                                            locationFieldTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.LOCATION_FIELD}
                                            budgetInputTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.BUDGET_INPUT}
                                            creditPlanSwitchTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.CREDIT_PLAN_SWITCH}
                                            savePreferencesButtonTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.SAVE_PREFERENCES_BUTTON}
                                            closeButtonTestId={PREFERENCES_SURVEY_MODAL_TEST_IDS.CLOSE_BUTTON}
                                            submitButtonText={submitButtonText}
                                            closeButtonText={closeButtonText}
                                            onCancel={onCancel}
                                        />
                                    );
                                }}
                            </UserPreferencesFormProvider>
                        )}
                    </div>
                </Modal.Layout.EmptyResponsive>
            </div>
        </Modal>
    );
};

const modalRootStyle = css({
    sm: {
        borderRadius: 2
    }
});

const homeMatchBasicEditModalRootStyle = css({
    sm: {
        width: "544px"
    }
});

const modalStyle = css({
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
    width: "100%",
    p: 0,

    sm: {
        width: "544px"
    }
});

const homeMatchBasicEditModalStyle = css({
    sm: {
        width: "544px"
    }
});

const contentStyle = css({
    width: "100%",
    p: 0,
    backgroundColor: "var(--colors-white)",
    display: "flex",
    flexDirection: "column",
    gap: 4,

    sm: {
        width: "544px",
        p: 3
    }
});

const homeMatchBasicEditContentStyle = css({
    sm: {
        width: "544px",
        p: 3
    }
});

const loaderWrapperStyle = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    minHeight: "100vh",

    sm: {
        minHeight: "300px"
    }
});

const formWrapperClassName = css({
    p: 2,

    md: {
        p: 0
    }
});

const homeMatchBasicEditFormWrapperClassName = css({
    p: 3,

    sm: {
        p: 0
    }
});

const headerStyle = css({
    display: "flex",
    gap: 2,
    alignItems: "flex-start"
});

const headerTextStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});
